<template>
  <Navbar>
    <template v-slot:navbarTitle>モニターシステム</template>
  </Navbar>

  <main id="main" class="container-fluid pt-3">
    <div class="clearfix mb-3">
      <div class="float-end">
        <router-link
          :to="{
            name: 'ManageInspectionMonitor',
          }"
          class="btn btn-outline-dark me-1"
        >
          <span class="d-block d-sm-none"
            ><i class="bi bi-arrow-left"></i
          ></span>
          <span class="d-none d-sm-block">一覧に戻る</span>
        </router-link>
        <button
          type="button"
          @click="showInspectionForm"
          class="btn btn-outline-success me-1"
        >
          <span class="d-block d-sm-none"><i class="bi bi-pencil"></i></span>
          <span class="d-none d-sm-block">編集</span>
        </button>
        <button class="btn btn-danger" v-on:click.prevent="removeData()">
          <span class="d-block d-sm-none"><i class="bi bi-trash"></i></span>
          <span class="d-none d-sm-block">削除</span>
        </button>
      </div>
      <h5 class="text-truncate">
        <i class="bi bi-clipboard me-1"></i>{{ inspection_type.name }} / {{ inspection.name }}
      </h5>
    </div>

    <div class="row">
      <div class="col-4">
        <h5>型式・型番</h5>
        <div class="card mb-3">
          <ul
            v-if="inspection.calls && inspection.calls.length > 0"
            class="list-group list-group-flush"
          >
            <li
              v-for="call in inspection.calls"
              :key="call.id"
              class="list-group-item d-flex justify-content-between"
            >
              <div class="text-break align-self-center">
                <span>{{ call.code }}</span>
              </div>
              <div class="text-nowrap">
                <button
                  type="button"
                  class="btn btn-link btn-sm text-secondary"
                  v-on:click.prevent="removeCall(call.id)"
                >
                  <i class="bi bi-trash-fill"></i>
                </button>
              </div>
            </li>
          </ul>
          <div v-else class="card-body">型式・型番はありません。</div>
        </div>
        <div class="d-sm-flex justify-content-between">
          <p>
            <i class="bi bi-plus-circle me-1"></i
            ><a
              href="#"
              v-on:click.prevent="showCallForm"
              class="text-muted text-decoration-none"
              >型式・型番を追加する</a
            >
          </p>
        </div>
      </div>

      <div class="col-8">
        <!-- inspection images -->
        <h5>参考画像</h5>
        <ul class="nav nav-tabs mb-2">
          <li class="nav-item" v-for="(tab, idx) in imageTabs" :key="idx">
            <a
              class="nav-link"
              :class="{ active: activeImageTab === idx }"
              href="#"
              @click.prevent="activeImageTab = idx"
              >ページ{{ idx + 1 }}</a
            >
          </li>
          <li class="nav-item" v-if="imageTabs.length < this.maxImageTabs">
            <a class="nav-link" href="#" @click.prevent="addImageTab">
              <i class="bi bi-plus"></i>
            </a>
          </li>
        </ul>
        <div class="row row-cols-2 row-cols-md-3 g-1 g-md-2">
          <div v-for="n in 6" :key="n - 1" class="col">
            <template
              v-if="
                getImagesByTab(activeImageTab) &&
                getImagesByTab(activeImageTab).find(
                  (img) => img.position === activeImageTab * 6 + n - 1,
                )
              "
            >
              <ImageGallery
                :files="[
                  getImagesByTab(activeImageTab).find(
                    (img) => img.position === activeImageTab * 6 + n - 1,
                  ),
                ]"
                thumbnail_class="object-fit-contain"
              >
                <template v-slot:thumbnail-operation-icons="{ file }">
                  <div class="d-flex justify-content-end p-1">
                    <button
                      class="btn btn-sm btn-outline-danger border-0"
                      @click="removeImage(file.id)"
                    >
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </template>
              </ImageGallery>
            </template>
            <template v-else>
              <button
                type="button"
                class="btn btn-outline-secondary w-100"
                @click="showImageForm(activeImageTab * 6 + n - 1)"
              >
                <i class="bi bi-plus"></i> 画像追加
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <InspectionCallForm
      ref="callForm"
      @insert-call="insertCall"
      :inspection_id="id"
      :inspection_type_id="inspection_type.id"
    />

    <Confirm ref="confirmDataDelete" @confirm-true="confirmDataDelete" />
    <Confirm ref="confirmCallDelete" @confirm-true="confirmCallDelete" />
    <Confirm ref="confirmImageDelete" @confirm-true="confirmImageDelete" />
    <ImageModal ref="imageModal" />
    <PreviewUpload
      ref="inspectionImageForm"
      accept="image/jpeg,image/png,image/gif,image/tiff,image/webp,.tif,.tiff"
      :upload-url="'manage/inspection-images/upload/'"
      @upload-complete="imageUploaded"
    />
  </main>

  <InspectionForm ref="inspectionForm" />
</template>

<script>
import ImageGallery from '@/components/AppImageGallery.vue'
import Navbar from '@/components/Navbar.vue'
import Confirm from '@/components/TheConfirm.vue'
import ImageModal from '@/components/TheImageModal.vue'
import PreviewUpload from '@/components/common/PreviewUpload.vue'
import InspectionCallForm from '../components/InspectionCallForm.vue'
import InspectionForm from '../components/InspectionForm.vue'
import store from '../store'

export default {
  name: 'ManageInspectionDetail',
  components: {
    Navbar,
    Confirm,
    InspectionCallForm,
    PreviewUpload,
    ImageModal,
    ImageGallery,
    InspectionForm,
  },

  props: {
    id: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      current_call_id: null,
      imageTabs: [0], // 最初は1つ
      activeImageTab: 0,
      maxImageTabs: 5,
      maxImagesPerTab: 6,
      position: null,
      positionTab: null,
    }
  },
  computed: {
    inspection() {
      return store.getters['inspection/detail']
    },
    inspection_type() {
      return store.getters['inspection/inspection_type']
    },
  },

  watch: {
    'inspection.images': {
      immediate: true,
      handler(newVal) {
        const maxPosition = Math.max(
          0,
          ...(newVal || []).map((img) => img.position || 0),
        )
        const neededTabs = Math.floor(maxPosition / this.maxImagesPerTab) + 1
        this.imageTabs = Array.from({ length: neededTabs }, (_, i) => i)
        // 現在の activeImageTab が存在しない場合は最後のタブをアクティブにする
        if (this.activeImageTab >= neededTabs) {
          this.activeImageTab = neededTabs - 1
        }
      },
    },
  },

  async created() {
    if (!this.inspection_type || this.inspection_type.id == null) {
      this.$router.push({
        name: 'ManageInspectionMonitor',
      })
      return
    }

    await this.getData(this.id).catch((error) => {
      if (error.status) {
        window.setTimeout(() => {
          this.$router.push({
            name: 'ManageInspectionMonitor',
          })
        }, this.$store.getters['alert/timeout'])
      }
    })
  },

  methods: {
    addImageTab() {
      if (this.imageTabs.length < this.maxImageTabs) {
        const newIdx = this.imageTabs.length
        this.imageTabs.push(newIdx)
        this.activeImageTab = newIdx
      }
    },
    getImagesByTab(tabIdx) {
      if (!this.inspection.images) return []
      // positionが全体連番の場合、グループごとにmaxImagesPerTab件ずつ分ける
      const start = tabIdx * this.maxImagesPerTab
      const end = start + this.maxImagesPerTab
      return this.inspection.images.filter(
        (img) => img.position >= start && img.position < end,
      )
    },
    showImageForm(position) {
      this.position = position
      this.positionTab = this.activeImageTab
      this.$refs.inspectionImageForm.show()
    },
    async imageUploaded(fileData) {
      try {
        fileData.inspection_id = this.id
        fileData.position = this.position
        fileData.tabIndex = this.positionTab ?? 0
        await store.dispatch('inspection_image/postData', fileData)
        await this.getData(this.id)
      } catch (e) {
        if (e.response?.status == 400) {
          this.$store.dispatch('alert/setErrorMessage', {
            message: e.response.data,
          })
        }
      } finally {
        this.position = null
        this.positionTab = null
      }
    },
    removeImage(imageId) {
      this.deleteImageId = imageId
      this.$refs.confirmImageDelete.showConfirm('', 'この画像を削除しますか？')
    },
    async confirmImageDelete() {
      try {
        await store.dispatch('inspection_image/deleteData', this.deleteImageId)
        this.deleteImageId = null
        await this.getData(this.id)
      } catch (e) {
        if (e.response?.status == 400) {
          this.$store.dispatch('alert/setErrorMessage', {
            message: e.response.data,
          })
        }
      }
    },
    getData(id) {
      return store.dispatch('inspection/fetchData', id)
    },
    removeData() {
      this.$refs.confirmDataDelete.showConfirm(
        'データを削除',
        'この検査資料を削除しますか？',
      )
    },
    async confirmDataDelete() {
      await store.dispatch('inspection/deleteData', this.id)
      this.$router.push({
        name: 'ManageInspectionMonitor',
      })
    },
    showCallForm() {
      this.$refs.callForm.show()
    },
    /**
     * 保存後に画面上の型式・型番リストに追加する
     * @param data
     */
    insertCall(data) {
      if (!('calls' in this.inspection)) {
        this.inspection.calls = []
      }
      this.inspection.calls.push(data)
    },
    removeCall(call_id) {
      this.current_call_id = call_id
      this.$refs.confirmCallDelete.showConfirm(
        'データを削除',
        'この型式・型番を削除しますか？',
      )
    },
    async confirmCallDelete() {
      await store.dispatch('inspection_call/deleteData', this.current_call_id)
      let index = this.inspection.calls.findIndex(
        ({ id }) => id === this.current_call_id,
      )
      this.inspection.calls.splice(index, 1)
      this.current_call_id = null
    },
    showInspectionForm() {
      this.$refs.inspectionForm.show(this.inspection)
    },
  },
}
</script>
